import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMapGroupStyles } from "../hooks/useMapGroupStyles";
import { recolorGroupSymbolSvgCached } from "../models/groupSymbolSvg";
import {
  MapAssetRow,
  MapAssetSortKey,
  filterMapAssetRows,
  sortMapAssetRows,
} from "../models/mapAssetRow";
import { groupStyleOf } from "../models/mapSettings";
import { zoneAt } from "../models/zoneContainment";
import { VehicleListRow } from "./VehicleListRow";
import { useAddressesByPosition } from "@/hooks/addresses/useAddressesByPosition";
import { useZoneOutlines } from "@/hooks/zone/useZoneOutlines";
import { clamp } from "@/utilities/clamp";

/** Below this much map, the list would take more of the screen than the map it belongs to. */
const HIDE_BELOW_MAP_WIDTH_PX = 900;
const DEFAULT_WIDTH_PX = 380;
const MIN_WIDTH_PX = 280;
/** Left free to the right of the list, so the map is never fully covered. */
const KEEP_FREE_PX = 24;
const RESIZE_HANDLE_PX = 10;
/** How far down the panel the resize edge reaches: the header, and not the list below it. */
const RESIZE_HANDLE_HEIGHT = 96;
/**
 * How many rows are drawn at once.
 *
 * Zoomed out over a whole region the view can hold the entire fleet, and every row carries three
 * progress bars — a few thousand of those is a page that stops responding. Past this the reader
 * is told how many are left and can search or zoom instead of scrolling through hundreds.
 */
const MAX_DRAWN_ROWS = 200;

interface Props {
  rows: MapAssetRow[];
  /** Where each vehicle is, for looking up its address and its zone. */
  positionById: ReadonlyMap<string, [number, number]>;
  /** Which Geotab groups each vehicle is in, for the group's own symbol. */
  groupIdsByDevice: ReadonlyMap<string, string[]>;
  /** Whether zones are switched on. Off, none are read and no row is tested against one. */
  showZones: boolean;
  /** The colour the map draws a vehicle in, so the rows match the markers. */
  colorOfDevice: (deviceId: string) => string;
  /** Width of the map itself — the list hides itself when there is too little of it left. */
  containerWidth: number;
  selectedDeviceId: string | null;
  onSelect: (deviceId: string) => void;
}

/**
 * The list of vehicles currently on the map, beside the map.
 *
 * It hides itself when the map gets narrow rather than offering a button for it: on a narrow
 * window a list this wide leaves no map to speak of, and a button the reader has to find first
 * does not help someone whose window is already too small.
 */
export function VehicleListPanel({
  rows,
  positionById,
  groupIdsByDevice,
  showZones,
  colorOfDevice,
  containerWidth,
  selectedDeviceId,
  onSelect,
}: Props) {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState<MapAssetSortKey>("name-asc");
  const [searchTerm, setSearchTerm] = useState("");
  const [width, setWidth] = useState(DEFAULT_WIDTH_PX);
  const [resizing, setResizing] = useState(false);
  const resizeStartRef = useRef<{ startX: number; startWidth: number } | null>(null);

  // A width of zero is the first frame, before the map has been measured. Treated as hidden, or
  // the list mounts and fetches for a window it may be far too wide for.
  const hidden = containerWidth === 0 || containerWidth < HIDE_BELOW_MAP_WIDTH_PX;
  const maxWidth = Math.max(MIN_WIDTH_PX, containerWidth - KEEP_FREE_PX);

  // A window that shrinks must not leave the list wider than the map behind it.
  useEffect(() => {
    if (containerWidth <= 0) return;
    setWidth(current => clamp(current, MIN_WIDTH_PX, maxWidth));
  }, [containerWidth, maxWidth]);

  // The drag is followed on the window, not on the handle: the pointer leaves a 10px strip at the
  // first quick movement, and a handle-bound listener would drop the drag there.
  useEffect(() => {
    if (!resizing) return;

    const handleMove = (event: MouseEvent) => {
      const start = resizeStartRef.current;
      if (!start) return;
      setWidth(clamp(start.startWidth + (event.clientX - start.startX), MIN_WIDTH_PX, maxWidth));
    };
    const handleUp = () => setResizing(false);

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [resizing, maxWidth]);

  // None of the orders needs an address, so the order is settled first and addresses are then
  // only looked up for the rows that will actually be drawn.
  const orderedRows = useMemo(() => sortMapAssetRows(rows, sortKey), [rows, sortKey]);

  const searching = searchTerm.trim().length > 0;

  const geocodedPositions = useMemo(() => {
    // While something is being searched for, every row has to be findable by place, so the whole
    // view is looked up. Idle, only the rows on screen are — the view can hold the entire fleet,
    // and the rest would be requests for rows nobody sees.
    const candidates = searching ? orderedRows : orderedRows.slice(0, MAX_DRAWN_ROWS);
    const positions = new Map<string, [number, number]>();
    for (const row of candidates) {
      const position = positionById.get(row.deviceId);
      if (position) {
        positions.set(row.deviceId, position);
      }
    }
    return positions;
  }, [orderedRows, positionById, searching]);

  const addressByDeviceId = useAddressesByPosition(geocodedPositions, !hidden);
  const zoneOutlines = useZoneOutlines(showZones && !hidden);
  const groupStyles = useMapGroupStyles();

  // Kept apart from the filtering below: describing a row does not depend on what is being
  // searched for, and together in one memo every keystroke rebuilt the whole array again.
  const describedRows = useMemo(
    () =>
      orderedRows.map(row => {
        const address = addressByDeviceId.get(row.deviceId);
        const position = positionById.get(row.deviceId);
        const zone = position ? zoneAt(position[0], position[1], zoneOutlines) : undefined;

        return address || zone ? { ...row, address, zoneName: zone?.name } : row;
      }),
    [orderedRows, addressByDeviceId, positionById, zoneOutlines],
  );

  const matchingRows = useMemo(
    () => filterMapAssetRows(describedRows, searchTerm),
    [describedRows, searchTerm],
  );

  /** A group's symbol for each vehicle that inherits one, so the rows show what the map shows. */
  const symbolByDeviceId = useMemo(() => {
    const symbols = new Map<string, string>();
    if (groupStyles.groups.every(style => !style.svg)) {
      return symbols;
    }

    for (const row of orderedRows) {
      const svg = groupStyleOf(groupIdsByDevice.get(row.deviceId) ?? [], groupStyles.groups)?.svg;
      if (svg) {
        symbols.set(row.deviceId, recolorGroupSymbolSvgCached(svg, colorOfDevice(row.deviceId)));
      }
    }
    return symbols;
  }, [orderedRows, groupIdsByDevice, groupStyles.groups, colorOfDevice]);

  const drawnRows = matchingRows.slice(0, MAX_DRAWN_ROWS);

  if (hidden) {
    return null;
  }

  return (
    <Paper
      elevation={3}
      sx={{
        position: "absolute",
        // Below the search bar, which owns the top of this corner.
        top: 64,
        left: 12,
        zIndex: 950,
        width,
        maxWidth,
        maxHeight: "calc(100% - 96px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        // While dragging the edge, the pointer sweeps across the list and would select its text.
        userSelect: resizing ? "none" : "auto",
      }}
    >
      <Box
        onMouseDown={event => {
          resizeStartRef.current = { startX: event.clientX, startWidth: width };
          setResizing(true);
        }}
        aria-hidden="true"
        sx={{
          position: "absolute",
          right: 0,
          top: 0,
          width: RESIZE_HANDLE_PX,
          // Only down the head of the panel. Over the full height it lay on top of the list's
          // own scrollbar, so reaching for the scrollbar started a resize instead.
          height: RESIZE_HANDLE_HEIGHT,
          cursor: "ew-resize",
          zIndex: 2,
          "&:hover": { bgcolor: "action.hover" },
        }}
      />

      <Stack spacing={1} sx={{ p: 1, pr: 2, borderBottom: 1, borderColor: "divider" }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {t("map.vehicleList.title")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("map.vehicles", { count: matchingRows.length })}
          </Typography>
        </Box>

        <TextField
          size="small"
          value={searchTerm}
          onChange={event => setSearchTerm(event.target.value)}
          label={t("map.vehicleList.search")}
        />

        <FormControl size="small">
          <InputLabel id="vehicle-list-sort">{t("map.vehicleList.sortBy")}</InputLabel>
          <Select
            labelId="vehicle-list-sort"
            value={sortKey}
            label={t("map.vehicleList.sortBy")}
            onChange={event => setSortKey(event.target.value as MapAssetSortKey)}
          >
            <MenuItem value="name-asc">{t("map.vehicleList.sort.nameAsc")}</MenuItem>
            <MenuItem value="name-desc">{t("map.vehicleList.sort.nameDesc")}</MenuItem>
            <MenuItem value="status">{t("map.vehicleList.sort.status")}</MenuItem>
            <MenuItem value="duration-desc">{t("map.vehicleList.sort.duration")}</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Box sx={{ overflowY: "auto" }}>
        {matchingRows.length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", p: 1.5 }}>
            {t("map.vehicleList.empty")}
          </Typography>
        ) : (
          drawnRows.map(row => (
            <VehicleListRow
              key={row.deviceId}
              row={row}
              selected={row.deviceId === selectedDeviceId}
              symbolSvg={symbolByDeviceId.get(row.deviceId)}
              onSelect={onSelect}
            />
          ))
        )}

        {matchingRows.length > drawnRows.length ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", p: 1.5 }}>
            {t("map.vehicleList.truncated", {
              shown: drawnRows.length,
              total: matchingRows.length,
              defaultValue: "{{shown}} von {{total}} angezeigt — suchen oder hineinzoomen",
            })}
          </Typography>
        ) : null}
      </Box>
    </Paper>
  );
}
